import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { uploadRateImport } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { downloadCsv, RATE_TEMPLATE } from "./importTemplates";
import { fmtDay } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import PageHeader from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const ACTION: Record<string, { label: string; tone: string }> = {
  CREATE_DESIGN: { label: "New design + rates", tone: "is-ok" },
  NEW_CARD: { label: "New rate card", tone: "is-ok" },
  NEW_VERSION: { label: "New rate version", tone: "is-ok" },
  HAS_DRAFT: { label: "Has a draft already", tone: "is-review" },
  ERROR: { label: "Fix the file", tone: "is-review" },
};

/**
 * Designs and rates from one file: check it (nothing saved), then import. Rates land as drafts,
 * so nothing changes what anyone is paid until the admin approves them on the Rate cards page.
 */
const KarkhanaRateImportPage = () => {
  const history = useHistory();
  const role = readRole();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<any>(null);
  const [busy, setBusy] = useState<"" | "check" | "import">("");

  useEffect(() => {
    if (!canManageKarkhana(role)) history.replace(kp.home());
  }, [role, history]);

  const send = async (f: File, commit: boolean) => {
    setBusy(commit ? "import" : "check");
    try {
      const res: any = await uploadRateImport(f, commit);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not read the file", type: "danger" });
        return;
      }
      setReport(res.data);
      if (commit) {
        notifications({ title: "Imported", message: `${res.data.saved} design(s) saved as drafts. Approve them on Rate cards.`, type: "success" });
      }
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const pick = (f?: File | null) => {
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) {
      notifications({ title: "Error", message: "Choose a CSV or Excel file", type: "danger" });
      return;
    }
    setFile(f);
    setReport(null);
    send(f, false);
  };

  if (!canManageKarkhana(role)) return null;
  const designs: any[] = report?.designs || [];
  const committed = report && report.saved !== null && report.saved !== undefined;

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Import designs and rates" back={kp.rateCards()} backLabel="Rate cards" />
      <p className="kw-lede">
        One row per design and work type. Download the template, fill it in Excel or Google Sheets, save as CSV and upload.
        Rates can be typed as on the sheet, e.g. <span className="kw-code">500/10.5</span> with unit <span className="kw-code">girah</span>.
        Missing designs are created. Rates are saved as <strong>drafts</strong>: nothing is paid at them until you approve each card.
      </p>
      <div className="kw-toolbar">
        <Button size="sm" outline onClick={() => downloadCsv("hkp-designs-rates-template.csv", RATE_TEMPLATE)}>
          Download template
        </Button>
        <Button size="sm" className="ResetBtn" disabled={!!busy} onClick={() => fileRef.current?.click()}>
          {busy === "check" ? "Checking…" : file ? "Choose another file" : "Choose file"}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" hidden onChange={(e) => pick(e.target.files?.[0])} />
      </div>

      {report ? (
        <div className="kw-card">
          <h3>
            {file?.name} · {report.rows} rows · {designs.length} designs
          </h3>
          <p className="kw-lede">
            {committed
              ? `${report.saved} saved as drafts.`
              : `${report.ready} ready to import${report.problems ? `, ${report.problems} need fixing in the file` : ""}.`}
          </p>
          {(report.rowErrors || []).map((e: any) => (
            <div key={e.row} className="kw-muted">
              Row {e.row}: {e.message}
            </div>
          ))}
          <table className="table table-sm mt-2">
            <thead>
              <tr>
                <th>Design</th>
                <th>What happens</th>
                <th>Rates</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {designs.map((d) => (
                <tr key={d.designNumber}>
                  <td>
                    <span className="kw-code">{d.designNumber}</span>
                    {d.designName ? <div className="kw-muted">{d.designName}</div> : null}
                  </td>
                  <td>
                    <span className={`kw-chip ${ACTION[d.action]?.tone || ""}`}>
                      {d.result === "SAVED" ? "Saved as draft" : d.result === "FAILED" ? "Not saved" : ACTION[d.action]?.label || d.action}
                    </span>
                    {(d.errors || []).map((e: string, i: number) => (
                      <div key={i} className="kw-muted">
                        {e}
                      </div>
                    ))}
                  </td>
                  <td>{d.lines}</td>
                  <td>{d.effectiveFrom ? fmtDay(d.effectiveFrom) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!committed ? (
            <Button className="ResetBtn" disabled={!!busy || !report.ready || !file} onClick={() => file && send(file, true)}>
              {busy === "import" ? "Importing…" : `Import ${report.ready} design${report.ready === 1 ? "" : "s"} as drafts`}
            </Button>
          ) : (
            <Link to={kp.rateCards()} className="btn btn-hastkala">
              Review and approve on Rate cards
            </Link>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default KarkhanaRateImportPage;
